import React, {useEffect, useState} from "react";
import {Box, Button, Radio, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";

// CSS-in-JS Styles
const styles = {
    options: {
        display: "flex",
        alignItems: "center",
        padding: "10px 15px",
        border: "1px solid #ddd", // Default border
        borderRadius: "8px",
        marginBottom: "10px",
        cursor: "pointer",
        transition: "all 0.3s ease",
    },
    selected: {
        border: "1px solid #0FB694", // Highlight selected border
        backgroundColor: "#F0FFF9", // Subtle green background
    },
};

const RadioQuestion = ({
                           data,
                           addQuestion,
                           deleteQuestionsAfterIndex,
                           goToNextQuestion,
                           isVisible,
                           addToUpdateQuestions,
                           deleteFromQuestionToUpdate,
                       }) => {
    const {t} = useTranslation();

    // State to track the selected choice
    const [selectedChoice, setSelectedChoice] = useState(null);
    const language = localStorage.getItem("i18nextLng");

    // Map `data?.currentValue` to the corresponding choice on mount
    useEffect(() => {
        if (data?.currentValue !== undefined && data?.choices) {
            const currentChoice = data.choices.find(
                (choice) => String(choice.value) === String(data.currentValue)
            );
            if (currentChoice) {
                setSelectedChoice(currentChoice); // Set the default selected choice
            }
        }
    }, [data?.currentValue, data?.choices]);

    // Debug: Log the data when the component mounts


    // The handler for when a radio button is selected
    const handleOptionClick = (choice) => {
        setSelectedChoice(choice); // Update the selected choice
        deleteQuestionsAfterIndex(data); // Delete subsequent questions
        deleteFromQuestionToUpdate(data); // Delete subsequent questions
    };

    // The handler for when the "Next" button is clicked
    const handleNextClick = () => {
        if (selectedChoice) {
            // // Log the payload (for demonstration purposes)
            // console.log("Payload:", {
            //     questionId: data.identifier,
            //     value: selectedChoice.value,
            // });

            // Add follow-up questions if the selected choice has a `question_object`
            if (data && selectedChoice && selectedChoice.value.toString() !== data.currentValue) {
                addToUpdateQuestions(data, selectedChoice.value);
            }
            if (selectedChoice?.question_object) {
                addQuestion(selectedChoice.question_object);
            } else if (data?.question_object) {
                addQuestion(data.question_object);
            } else {
                goToNextQuestion(); // Move to the next question
            }

        }
    };

    function handleSkipClick() {
        if (data?.associated_step_id === "4c2ea3bd-831a-4a53-8cc7-5ec1b26d628b") {
            if (data?.question_object) {

                addQuestion(data?.question_object);
            } else {
                // Otherwise, move to the next question
                goToNextQuestion(null, true);
            }
        } else {
            goToNextQuestion(null, true);
        }
    }

    return (
        <Box
            sx={{
                padding: "20px",
                display: "flex",
                flexDirection: "column",
            }}
        >
            {/* Render the main question */}
            <Typography variant="subtitle1" sx={{mb: 2}}>
                {language === "fr" ? data?.question_fr : data.question}
            </Typography>

            {/* Render each choice as a radio button */}
            <Box>
                {data?.choices.map((choice) => {
                    const isSelected = selectedChoice?.value === choice.value; // Compare `value` instead of `id`

                    // Debug: Log the selected state for each choice


                    return (
                        <p
                            key={choice.value} // Use `value` as the unique key
                            onClick={() => handleOptionClick(choice)} // Handle option click
                            style={{
                                ...styles.options,
                                ...(isSelected ? styles.selected : {}), // Apply conditional styling for selected state
                            }}
                        >
                            <Radio
                                checked={isSelected}
                                onChange={() => handleOptionClick(choice)} // Update the selected value
                                checkedIcon={
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z"
                                            fill="#0FB694"
                                        />
                                        <path
                                            d="M9 12L11 14L15 10"
                                            stroke="white"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                }
                            />
                            <Typography
                                variant="body1"
                                sx={{
                                    marginLeft: "10px",
                                    fontWeight: "500",
                                    color: "#555",
                                }}
                            >
                                {t(String(choice.value))}
                            </Typography>
                        </p>
                    );
                })}
            </Box>

            {/* Next Button */}


            <div style={{display: "flex", gap: "10px", alignItems: "center"}}>
                {isVisible(data) && (
                    <Button
                        variant="contained"
                        color="secondary"
                        style={{
                            height: "50px",
                            width: data?.associated_step_id !== "4c2ea3bd-831a-4a53-8cc7-5ec1b26d628b" ? "50%" : "50%", // Each button takes 45% of the container width
                            marginTop: "20px",
                        }}
                        onClick={handleNextClick}
                        disabled={!selectedChoice} // Disable if required fields are empty
                    >
                        {data?.last_step_question_2 ? t("finish") : t("next")}
                    </Button>
                )}
                {isVisible(data) && (
                    <Button
                        style={{
                            height: "50px",
                            width: "50%", // Each button takes 45% of the container width
                            marginTop: "20px",
                        }}
                        color="secondary"
                        variant="contained"
                        onClick={handleSkipClick}
                    >
                        {t("skip")}
                    </Button>
                )}
            </div>
        </Box>
    );
};

export default RadioQuestion;